import { Button, Descriptions, Grid } from 'antd';
import { noop } from 'lodash/fp';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
import { type WebsiteModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/WebsiteModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import useImportLocationsModal, { type OnImportHandler } from './useImportLocationsModal';
import WebsiteModuleActions from './WebsiteModuleActions';
import WebsiteModuleForm from './WebsiteModuleForm';

export type WebsiteModulePageProps = { module: WebsiteModuleWithPermissionsSpecsFragment };

const WebsiteModulePage = ({ module }: WebsiteModulePageProps) => {
    const { t } = useTranslation(['websiteModuleDetails', 'common']);

    const navigate = useNavigate();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('websiteModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    const [onImport, setOnImport] = useState<OnImportHandler>(noop);
    const modal = useImportLocationsModal(onImport);
    const permissions = {
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const footer = [
        <Button onClick={modal.openModal}>{t('websiteModuleDetails:buttons.importLocations')}</Button>,
        <Button form="websiteModuleMainDetailsForm" htmlType="submit" type="primary">
            {t('websiteModuleDetails:buttons.update')}
        </Button>,
    ];

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<WebsiteModuleActions module={module} permissions={permissions} />}
            footer={footer}
            onBack={() => navigate('/admin/system/modules')}
            title={screens.md ? t('websiteModuleDetails:title', { name: module.displayName }) : module.displayName}
        >
            <WebsiteModuleForm module={module} setOnImport={setOnImport} />
            {modal.render()}
        </ConsolePageWithHeader>
    );
};

export default WebsiteModulePage;
